import { useState } from "react";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { EditableEvidence, ResolveCandidate } from "@/lib/outline-import-types";
import { useOutlineResolve } from "@/hooks/useOutlineImport";
import PaperTypeahead from "./PaperTypeahead";

interface Props {
  evidence: EditableEvidence;
  conceptId: string;
  onSwap: (paperId: string, title: string, authors: string) => void;
  onSkip: () => void;
}

export default function EvidenceRow({ evidence, conceptId, onSwap, onSkip }: Props) {
  const [open, setOpen] = useState(false);
  const [s2Open, setS2Open] = useState(false);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<ResolveCandidate[] | null>(null);
  const { resolveCitation, addPaper } = useOutlineResolve(conceptId);
  const matched = evidence.selectedPaperId !== null;

  async function runSearch() {
    setSearching(true);
    setCandidates(null);
    try {
      setCandidates(await resolveCitation(evidence.citation_marker, evidence.excerpt_text));
    } catch {
      toast.error("Semantic Scholar search failed");
      setCandidates([]);
    } finally {
      setSearching(false);
    }
  }

  async function pickCandidate(c: ResolveCandidate) {
    setAddingId(c.s2_paper_id);
    try {
      const added = await addPaper(c.s2_paper_id);
      setS2Open(false);
      onSwap(added.paper_id, added.title, added.authors);
      toast.success("Added to corpus and matched");
    } catch {
      toast.error("Could not add that paper");
    } finally {
      setAddingId(null);
    }
  }

  if (evidence.skipped) {
    return (
      <div className="text-xs text-muted-foreground italic p-2">Skipped</div>
    );
  }

  if (!matched) {
    return (
      <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded p-2 text-sm">
        <span className="flex-1 text-amber-900 dark:text-amber-100">
          ⚠ No corpus match for <code>{evidence.citation_marker}</code>
        </span>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline">Pick ▾</Button>
          </PopoverTrigger>
          <PopoverContent className="w-96">
            <PaperTypeahead conceptId={conceptId} onPick={(p) => { setOpen(false); onSwap(p.id, p.title, p.authors); }} />
          </PopoverContent>
        </Popover>
        <Popover
          open={s2Open}
          onOpenChange={(o) => { setS2Open(o); if (o && candidates === null && !searching) void runSearch(); }}
        >
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline">Find on S2 ▾</Button>
          </PopoverTrigger>
          <PopoverContent className="w-[28rem]">
            <div className="mb-2 text-xs text-muted-foreground">
              Search Semantic Scholar, then add to your corpus to match.
            </div>
            {searching && <div className="p-2 text-sm text-muted-foreground">Searching…</div>}
            {!searching && candidates?.length === 0 && (
              <div className="p-2 text-sm text-muted-foreground">No candidates found.</div>
            )}
            {!searching && candidates && candidates.map((c) => (
              <button
                key={c.s2_paper_id}
                disabled={addingId !== null}
                className="block w-full text-left p-2 hover:bg-muted/60 rounded text-sm disabled:opacity-50"
                onClick={() => void pickCandidate(c)}
              >
                <div className="font-medium">{c.title}</div>
                <div className="text-xs text-muted-foreground">
                  {[c.authors, c.year].filter(Boolean).join(" · ")}
                  {addingId === c.s2_paper_id && " — adding…"}
                </div>
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <Button size="sm" variant="ghost" aria-label="skip evidence" onClick={onSkip}>×</Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 border rounded p-2 text-sm">
      <div className="flex-1">
        <div className="font-medium">{evidence.selectedPaperTitle}</div>
        <div className="text-xs text-muted-foreground">{evidence.selectedPaperAuthors}</div>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost">Swap ▾</Button>
        </PopoverTrigger>
        <PopoverContent className="w-96">
          {evidence.match && evidence.match.alternatives.length > 0 && (
            <>
              <div className="text-xs text-muted-foreground mb-2">Alternatives</div>
              {evidence.match.alternatives.map((alt) => (
                <button
                  key={alt.paper_id}
                  className="block w-full text-left p-2 hover:bg-gray-100 text-sm"
                  onClick={() => { setOpen(false); onSwap(alt.paper_id, alt.title, alt.authors); }}
                >
                  <div className="font-medium">{alt.title}</div>
                  <div className="text-xs text-muted-foreground">{alt.authors}</div>
                </button>
              ))}
              <div className="border-t my-2" />
            </>
          )}
          <PaperTypeahead conceptId={conceptId} onPick={(p) => { setOpen(false); onSwap(p.id, p.title, p.authors); }} />
        </PopoverContent>
      </Popover>
      <Button size="sm" variant="ghost" aria-label="skip evidence" onClick={onSkip}>×</Button>
    </div>
  );
}
